//
// Copyright (c) 2024 Comcast
//

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import startCase from 'lodash/startCase'

import { diffObjectsDeep, walkLeavesDeep } from 'utils/object'

import colors from 'components/utils/colors'

const StyledBody = styled.div`
`

const InfoLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  height: 32px;
  color: ${colors.contentSecondaryActive};
  padding: 0 12px 4px 12px;
`

const TabSections = styled.div`
  background: ${colors.secondary95};
  padding: 8px 12px 8px 12px;
`

const TabSection = styled.div`
  padding-bottom: 2px;
`

const TabSectionHeader = styled.div`
  color: #000;
  font-family: Exo;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  padding: 8px 0 4px 0;
`

const TabSectionBody = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.secondary20};
  padding-bottom: 4px;
`

const GroupSection = styled.div`
  background: ${colors.secondary90};
  color: ${colors.secondary20};
  padding: 8px;
  margin-bottom: 8px;
`

const GroupSectionHeader = styled.div`
  font-family: Exo;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
`

const GroupSectionBody = styled.div`
  font-size: 12px;
  line-height: 18px;
`

const ConfirmDialogBody = ({ initialValues, fieldParams, additionalProps }) => {
  const { t } = useTranslation()
  const infoLine = t('common.confirmationInfoLine')
  const tabsMap = {}

  const fieldParamsMap = {}
  const fieldGroupsMap = {}
  fieldParams.forEach(g => {
    const separator = (g.groupPrefix && g.group) ? '.' : ''
    const key = (g.groupPrefix || '') + separator + g.group
    fieldParamsMap[key] = {}
    fieldGroupsMap[key] = g
    g.fieldParams.forEach(f => {
      fieldParamsMap[key][f.name] = f
    })
  })

  const changedValues = diffObjectsDeep(additionalProps.values, initialValues)

  // iterate over changed values and build tabsMap
  walkLeavesDeep(changedValues, (key, value, level, parentKeys) => {
    const parentKeysString = parentKeys.join('.')
    let fieldParent = fieldParamsMap[parentKeysString]
    let fieldGroup = fieldGroupsMap[parentKeysString]
    let fieldKey = key

    // process object values
    // e.g. for iconParams.filename and parentKeys=['iconParams'] remove last key
    // to find object at upper level and get description of `iconParams` field instead
    if (!fieldParent && parentKeys) {
      fieldKey = parentKeys.at(-1)
      const newParentKeys = parentKeys.slice(0, -1)
      const newParentKeysString = newParentKeys.join('.')
      fieldParent = fieldParamsMap[newParentKeysString]
      fieldGroup = fieldGroupsMap[newParentKeysString]
    }

    const field = fieldParent[fieldKey]
    const tabKey = fieldGroup.tabId

    // add empty tab if it doesn't exist
    if (!tabsMap[tabKey]) {
      tabsMap[tabKey] = {
        orphanFields: [],
        orphanFieldsMap: [],
        groupsOrder: [],
        groupsMap: [],
      }
    }
    const propName = field.label

    // if the field doesn't have group title, it is orphan (top level) field
    const fieldGroupTitle = fieldGroup.groupTitle || startCase(fieldGroup.group)
    const isOrphan = Boolean(!fieldGroupTitle || fieldGroup.tabId === fieldGroup.group)

    if (isOrphan) {
      if (!tabsMap[tabKey].orphanFieldsMap[propName]) {
        tabsMap[tabKey].orphanFields.push(propName)
        tabsMap[tabKey].orphanFieldsMap[propName] = propName
      }
    } else { // field in group
      // add empty group if it doesn't exist
      if (!tabsMap[tabKey].groupsMap[fieldGroupTitle]) {
        tabsMap[tabKey].groupsOrder.push(fieldGroupTitle)
        tabsMap[tabKey].groupsMap[fieldGroupTitle] = {
          fields: [],
          fieldsMap: {},
        }
      }
      // add field to group
      if (!tabsMap[tabKey].groupsMap[fieldGroupTitle].fieldsMap[propName]) {
        tabsMap[tabKey].groupsMap[fieldGroupTitle].fields.push(propName)
        tabsMap[tabKey].groupsMap[fieldGroupTitle].fieldsMap[propName] = propName
      }
    }
  })

  return (
    <StyledBody>
      <InfoLine data-test='confirm-info-line'>{infoLine}</InfoLine>
      <TabSections data-test='confirm-changes'>
        {Object.keys(tabsMap).map(tabKey => {
          const tab = tabsMap[tabKey]
          const tabName = startCase(tabKey)
          const orphanFields = tab.orphanFields.join(', ')

          return (
            <TabSection key={tabKey} data-test='confirm-tab-section'>
              <TabSectionHeader data-test='confirm-tab-section-header'>{tabName}</TabSectionHeader>
              <TabSectionBody data-test='confirm-tab-section-body'>{orphanFields}</TabSectionBody>
              {tab.groupsOrder.map(groupTitle => {
                const groupFields = tab.groupsMap[groupTitle].fields.join(', ')

                return (
                  <GroupSection key={groupTitle} data-test='confirm-group-section'>
                    <GroupSectionHeader data-test='confirm-group-section-header'>{groupTitle}</GroupSectionHeader>
                    <GroupSectionBody data-test='confirm-group-section-body'>{groupFields}</GroupSectionBody>
                  </GroupSection>
                )
              })}
            </TabSection>
          )
        })}
      </TabSections>
    </StyledBody>
  )
}

ConfirmDialogBody.propTypes = {
  initialValues: PropTypes.object.isRequired,
  fieldParams: PropTypes.array.isRequired,
  additionalProps: PropTypes.object.isRequired,
}

export default ConfirmDialogBody
